// @flow
import { parseHashParams } from '../utils/commons';
import type { PlatformAdapter } from './PlatformAdapter';

class ConnectAdapter implements PlatformAdapter {
    getProjectUrl(projectKey, projectStyle, params): string {
        const selectedItem = ['classic', undefined].includes(projectStyle)
            ? 'com.atlassian.jira.jira-projects-plugin:components-page'
            : `com.atlassian.plugins.atlassian-connect-plugin:${process.env.REACT_APP_KEY}__nextgen-components-jira`;
        const urlSearchParams = new URLSearchParams(params || '');
        urlSearchParams.set('selectedItem', selectedItem);
        return `/projects/${projectKey}?${decodeURIComponent(urlSearchParams.toString())}`;
    }

    openProjectUrl(projectKey, projectStyle, params): void {
        const url = this.getProjectUrl(projectKey, projectStyle, params);
        this.openNewTab(url);
    }

    navigateHomePage(): void {
        window.AP.navigator.go('addonModule', {
            addonKey: 'octo-jexo-component-management',
            moduleKey: 'octo-components-management-page-jira',
        });
    }

    async isGlobalContext(): Promise<boolean> {
        // if current URL does not include 'projectKey' query param it's global context
        return !/(projectKey|projectId)=([^&]*)/.exec(window.location.search)?.length;
    }

    refreshJiraIssuePage(): void {
        window?.AP?.jira?.refreshIssuePage?.();
    }

    openNewTab(path: string): void {
        this.getCurrentUrl().then((instanceUrl) => {
            const url = new URL(instanceUrl);
            window?.open?.(`${url.origin}/${path}`, '_blank');
        });
    }

    async getLocationFromCurrentModuleKey(): Promise<string> {
        return window?.location?.pathname;
    }

    getToken(): Promise<string> {
        return window?.AP?.context?.getToken?.();
    }

    resizeWindow(width: string, height: string) {
        window?.AP?.resize?.(width, height);
    }

    getJiraContext() {
        // @ts-ignore
        return window?.AP?.context?.getContext?.();
    }

    getRouteParams(): Promise<URLSearchParams> {
        return parseHashParams(window?.AP?.getLocation);
    }

    updateRouteParams(params: URLSearchParams) {
        const hash = decodeURIComponent(params.toString());
        window?.AP?.getLocation?.((location: string) => {
            const url = new URL(location);
            const relativeURL = `${url.pathname}${url.search ?? ''}`;

            // If current URL has no hash params and params object is empty, do nothing...
            if (!url.hash?.length && params.toString().length === 0) {
                return;
            }

            // Only update if the current location does not match the desired relative URL with hash
            if (!location.endsWith(`${relativeURL}#${hash}`)) {
                window.AP.navigator.go('site', {
                    relativeUrl: `${relativeURL}#${hash}`,
                });
            }
        });
    }

    isForge(): boolean {
        return false;
    }

    openIssueDialog(issueKey: string, onClose?: (issueKeyOrErr: string) => void) {
        // @ts-ignore
        window?.AP?.jira?.openIssueDialog?.(issueKey, onClose);
    }

    async initializeTheming() {
        if (window?.AP) {
            await window.AP.theming.initializeTheming();
        }
    }

    getCurrentUrl = async (): Promise<string> => {
        if (!window?.AP) return Promise.resolve(undefined);
        return new Promise((resolve, reject) => {
            window.AP.getLocation((instanceFullURL: string) => {
                try {
                    resolve(instanceFullURL);
                } catch (error) {
                    reject(error);
                }
            });
        });
    };

    requestJira = async (path: string, options?: any) => {
        // @ts-ignore
        const response = await window?.AP?.request?.(path, options);
        return typeof response?.body === 'string' ? JSON.parse(response.body) : undefined;
    };

    invokeRemote = async (method: string, path: string, args: any) => {
        if (!args?.anonymous) {
            const atlassianToken = await window?.AP?.context?.getToken?.();
            args = {
                ...args,
                headers: { accept: 'application/json', ...args?.headers, authorization: `JWT ${atlassianToken}` },
            };
        }
        delete args?.anonymous;
        const response = await fetch(path, {
            method,
            ...args,
        });
        return response.json();
    };
}
export default ConnectAdapter;
